// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import type { ItemData } from '@aps_sdk/data-management';
import { type ProjectId, type AutodeskCloudModel, useEvolvePaginatedGet } from '@evolvemep/api-ts';

import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { useGetAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isNotNil, isNil } from 'helpers/isNotNil';

type Opts = {
  hideInaccessible?: boolean;
};

export const useAutodeskModels = (projectId: ProjectId | undefined, { hideInaccessible }: Opts = {}) => {
  const { dataClient, getAutodeskProject } = useAutodeskAuth();
  const getDefaultOpts = useCallback(
    () => ({
      defaultConfig: {
        params: {
          projectId,
        },
      },
    }),
    [projectId],
  );

  const {
    setDefaultOpts: setDefaultOptsModels,
    fetchPage,
    reset,
    searchHandler,
  } = useEvolvePaginatedGet(`shop/autodesk/autodeskModel`, getDefaultOpts());

  useEffect(() => {
    setDefaultOptsModels(getDefaultOpts());
  }, [getDefaultOpts, setDefaultOptsModels]);

  // TODO: Update this to be paginated, instead.
  const [models, loading, { setWait }] = useGetAllDataFromFetcher(fetchPage, {}, true);
  useEffect(() => {
    setWait(isNil(projectId));
  }, [projectId, setWait]);

  const [modelCache, setModelCache] = useState<Record<AutodeskCloudModel['urn'], ItemData | null>>({});
  const modelCacheRef = useRef(modelCache);
  useEffect(() => {
    modelCacheRef.current = modelCache;
  }, [modelCache]);

  const [loadingModels, setLoadingModels] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoadingModels(true);
    void Promise.all(
      models.map(async (model) => {
        if (!(model.urn in modelCacheRef.current)) {
          const project = await getAutodeskProject(model.autodeskHubId, model.autodeskProjectId);
          if (cancelled) return;
          if (isNotNil(project)) {
            await dataClient
              .getItem(model.autodeskProjectId, model.urn)
              // Catch any error and set the model to `null`
              // which will show a "No access" error to the user
              .catch((err) => {
                // oxlint-disable-next-line no-console
                console.error(err);
                return null;
              })
              .then((res) => {
                if (!cancelled) {
                  setModelCache((c) => ({
                    ...c,
                    [model.urn]: res?.data ?? null,
                  }));
                }
              });
          } else {
            setModelCache((c) => ({
              ...c,
              [model.urn]: null,
            }));
          }
        }
      }),
    ).finally(() => {
      if (!cancelled) setLoadingModels(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dataClient, models, getAutodeskProject]);

  const filteredModels = useMemo(() => {
    if (hideInaccessible) {
      return models.filter((m) => isNotNil(modelCache[m.urn]));
    }
    return models;
  }, [hideInaccessible, modelCache, models]);

  return {
    models: filteredModels,
    searchHandler,
    modelCache,
    loading: loading || loadingModels,
    reset,
  };
};
